import type {
  MargoSummaryFieldViewModel,
  MargoSummaryViewModel,
} from './MargoSummary.types'

type UnknownRecord = Record<string, unknown>

interface OrderedMargoSummaryField extends MargoSummaryFieldViewModel {
  order: number
}

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isDefined = <T>(value: T | undefined): value is T => value !== undefined

const toDisplayString = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value.trim() || undefined
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  return undefined
}

const humanizeFieldKey = (key: string) => {
  const words = key
    .replace(/^mom_/, '')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[:_-]+/g, ' ')
    .trim()

  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key
}

const adaptField = (
  field: unknown,
  sourceIndex: number,
): OrderedMargoSummaryField | undefined => {
  if (!isRecord(field)) return undefined

  const value = toDisplayString(field.value)
  const key = toDisplayString(field.key) ?? `field-${sourceIndex}`

  if (!value) return undefined

  return {
    id: `${key}-${sourceIndex}`,
    label: humanizeFieldKey(key),
    value,
    order:
      typeof field.order === 'number' && Number.isFinite(field.order)
        ? field.order
        : sourceIndex,
  }
}

export const adaptMargoSummaryResponse = (
  response: unknown,
): MargoSummaryViewModel | undefined => {
  if (!isRecord(response)) return undefined

  const summary = toDisplayString(response.summary)
  const rawFields = Array.isArray(response.fields) ? response.fields : []
  const fields = rawFields
    .map(adaptField)
    .filter(isDefined)
    .sort((first, second) => first.order - second.order)
    .map(({ order: _order, ...field }) => field)

  if (!summary && fields.length === 0) return undefined

  return { summary, fields }
}
